import { Fragment, useState } from "react";
import { Icon } from "../components/Icon";
import { Btn, Card, PageHead, Seg, Skeleton, Toggle, NeedsData } from "../components/kit";
import { StatusDot } from "../components/StatusDot";
import {
  useLessonsAdmin, useLessonsCatalog, useTracks, useUsers, useOrgs, useConfig,
  saveTrack, deleteTrackReq, assignItemReq, saveLesson, type TrackRow, type AdminLesson,
} from "../lib/admin";

const LESSON_TYPES = ["tools", "skills", "mindset"] as const;
type DraftLesson = { id?: string; title: string; type: string; objective: string; difficulty: number; template: string; status: string; durationMinutes: string; asksTimeSaved: boolean; teachMistake: boolean };
const BLANK_LESSON: DraftLesson = { title: "", type: "tools", objective: "", difficulty: 2, template: "", status: "live", durationMinutes: "", asksTimeSaved: false, teachMistake: true };

export function CurriculumGod() {
  const [tab, setTab] = useState("Tracks");
  return (
    <div className="page">
      <PageHead title="Curriculum & Prompts" sub="Author lessons + tracks, assign them, and tune the prompts that drive the engine."
        actions={<div className="seg g">{["Tracks", "Lessons", "Prompt editor", "Selector"].map((t) => <button key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>{t}</button>)}</div>} />

      {tab === "Tracks" && <TracksTab />}
      {tab === "Lessons" && <LessonsTab />}

      {tab === "Prompt editor" && (
        <div className="page" style={{ padding: 0 }}>
          <Card title="Coach system prompt" icon="file-code-2" iconColor="var(--g-violet)">
            <NeedsData icon="file-code-2">
              The Coach prompt isn't a single editable string — it's built per fade-level in code (<code>worker/src/lessons.ts</code>, the fade-ladder system-prompt builder) from the lesson objective, the user's profile and the current fade level. Making it editable here needs a versioned prompt store (a <code>prompts</code> table + a typed builder that reads it) so edits can deploy and roll back without shipping the worker. Lesson-level prompts <em>are</em> editable today — see the <b>Lessons</b> tab's per-lesson template.
            </NeedsData>
          </Card>
        </div>
      )}

      {tab === "Selector" && <SelectorTab />}
    </div>
  );
}

/* ===== Selector tuning (live, operator-editable) ===== */
function SelectorTab() {
  const cfg = useConfig();
  const sel = cfg?.platform.selector;
  const [draft, setDraft] = useState<{ leverage: number; readiness: number; fit: number; interleave: boolean } | null>(null);
  const view = draft ?? sel ?? null;
  const [saving, setSaving] = useState(false);
  // Pull edits into a local draft once config loads, so sliders are responsive.
  const start = (patch: Partial<NonNullable<typeof view>>) => sel && setDraft({ ...(draft ?? sel), ...patch });
  const sum = view ? view.leverage + view.readiness + view.fit : 0;
  const dirty = !!(sel && draft && (draft.leverage !== sel.leverage || draft.readiness !== sel.readiness || draft.fit !== sel.fit || draft.interleave !== sel.interleave));
  const save = () => {
    if (!cfg || !draft) return;
    setSaving(true);
    Promise.all([
      cfg.setConfig("selector_leverage_weight", draft.leverage),
      cfg.setConfig("selector_readiness_weight", draft.readiness),
      cfg.setConfig("selector_fit_weight", draft.fit),
      cfg.setConfig("selector_interleave", draft.interleave),
    ]).finally(() => { setSaving(false); setDraft(null); });
  };
  const WEIGHTS: [keyof NonNullable<typeof view>, string, string][] = [
    ["leverage", "Leverage weight", "How much real-work impact drives ordering"],
    ["readiness", "Readiness weight", "Prerequisite & fade gating"],
    ["fit", "Fit weight", "Role & stack relevance"],
  ];
  return (
    <Card title="Selector tuning" icon="sliders-horizontal" pad={false}
      action={<Btn variant="live" size="sm" icon="check" disabled={!dirty || saving} onClick={save}>{saving ? "Saving…" : "Save weights"}</Btn>}>
      {view === null ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={28} />)}</div>
      ) : (
        <div className="set-list">
          {WEIGHTS.map(([key, title, desc]) => (
            <div className="set-row" key={key}>
              <span className="set-ic"><Icon name="settings-2" size={17} /></span>
              <div className="set-body"><div className="set-t">{title}</div><div className="set-d">{desc}</div></div>
              <div className="cfg-slider">
                <input type="range" min={0} max={1} step={0.05} value={view[key] as number} onChange={(e) => start({ [key]: Number(e.target.value) } as Partial<NonNullable<typeof view>>)} />
                <span className="budget-val mono">{(view[key] as number).toFixed(2)}</span>
              </div>
            </div>
          ))}
          <div className="set-row">
            <span className="set-ic"><Icon name="shuffle" size={17} /></span>
            <div className="set-body"><div className="set-t">Type interleave</div><div className="set-d">Rotate Tools → Skills → Mindset. Off = pick the single top-scored lesson.</div></div>
            <Toggle on={view.interleave} onChange={(v) => start({ interleave: v })} />
          </div>
          <div className="set-row">
            <span className="set-ic"><Icon name="sigma" size={17} /></span>
            <div className="set-body"><div className="set-t">Weight sum</div><div className="set-d">Weights are relative — they don't need to total 1, but balance matters.</div></div>
            <div className={`budget-val mono ${Math.abs(sum - 1) > 0.001 ? "warn" : ""}`}>{sum.toFixed(2)}</div>
          </div>
        </div>
      )}
      <div className="muted-sm" style={{ padding: 16 }}>Live weights from <code>worker/src/lessons.ts</code> — edits apply to the next <code>/lesson/next</code> selection, no deploy.</div>
    </Card>
  );
}

/* ===== Lessons (live catalog + editor) ===== */
function LessonsTab() {
  const { lessons: catalog, reload } = useLessonsCatalog();
  const lessons = catalog ?? [];
  const [draft, setDraft] = useState<DraftLesson | null>(null); // open editor when non-null
  const [assignId, setAssignId] = useState<string | null>(null); // lesson row showing assign controls

  const startNew = () => setDraft({ ...BLANK_LESSON });
  const startEdit = (l: AdminLesson) =>
    setDraft({
      id: l.id, title: l.title, type: l.type, objective: l.objective, difficulty: l.difficulty,
      template: l.template ?? "", status: l.status,
      durationMinutes: l.durationMinutes == null ? "" : String(l.durationMinutes),
      asksTimeSaved: l.asksTimeSaved === true,
      teachMistake: l.teachMistake !== false,
    });

  return (
    <Card title="Lesson catalog" icon="file-code-2" count={`${lessons.length} lessons`}
      action={<Btn variant="live" size="sm" icon={draft ? "x" : "plus"} onClick={() => (draft ? setDraft(null) : startNew())}>{draft ? "Close" : "New lesson"}</Btn>} pad={false}>
      {draft && <LessonEditor draft={draft} onChange={setDraft} onSaved={() => { setDraft(null); reload(); }} onCancel={() => setDraft(null)} />}
      <div className="gtable lessons-g">
        <div className="gt-head"><span>Lesson</span><span>Type</span><span>Difficulty</span><span>Objective</span><span>Status</span><span></span></div>
        {lessons.map((l) => (
          <Fragment key={l.id}>
            <div className="gt-row static">
              <span className="gt-strong">{l.title}</span>
              <span className="gt-mut">{l.type}</span>
              <span className="gt-mono">L{l.difficulty}</span>
              <span className="gt-mut ellip">{l.objective}</span>
              <span className="gt-status"><StatusDot s={l.status} />{l.status}</span>
              <span className="gt-go" style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                <button className="icon-mini" title="Assign lesson" onClick={() => setAssignId((id) => (id === l.id ? null : l.id))}><Icon name="user-plus" size={14} /></button>
                <button className="icon-mini" title="Edit lesson" onClick={() => startEdit(l)}><Icon name="pencil" size={14} /></button>
              </span>
            </div>
            {assignId === l.id && (
              <div style={{ gridColumn: "1 / -1", padding: "0 8px 12px", borderBottom: "1px solid var(--g-line)" }}>
                <AssignControls itemType="lesson" itemId={l.id} onDone={() => setAssignId(null)} />
              </div>
            )}
          </Fragment>
        ))}
        {catalog === null && <div className="gt-empty">Loading the live catalog…</div>}
        {catalog !== null && lessons.length === 0 && <div className="gt-empty">No lessons yet — author the first one.</div>}
      </div>
    </Card>
  );
}

function LessonEditor({ draft, onChange, onSaved, onCancel }: { draft: DraftLesson; onChange: (d: DraftLesson) => void; onSaved: () => void; onCancel: () => void }) {
  const [saving, setSaving] = useState(false);
  const set = (patch: Partial<DraftLesson>) => onChange({ ...draft, ...patch });
  const canSave = draft.title.trim().length > 0 && draft.objective.trim().length > 0;
  const save = () => {
    if (!canSave || saving) return;
    setSaving(true);
    const parsedDuration = parseInt(draft.durationMinutes, 10);
    saveLesson({
      ...(draft.id ? { id: draft.id } : {}),
      title: draft.title.trim(), type: draft.type, objective: draft.objective.trim(),
      difficulty: draft.difficulty, template: draft.template, status: draft.status,
      durationMinutes: Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : null,
      asksTimeSaved: draft.asksTimeSaved,
      teachMistake: draft.teachMistake,
    })
      .then(onSaved)
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ padding: "16px", borderBottom: "1px solid var(--g-line)", background: "var(--g-raised)" }}>
      <div className="muted-sm" style={{ marginBottom: 10, fontWeight: 600 }}>{draft.id ? "Edit lesson" : "New lesson"}</div>
      <input className="ginput" placeholder="Lesson title (e.g. Draft a PRD with AI)" value={draft.title} onChange={(e) => set({ title: e.target.value })} style={{ marginBottom: 10 }} />
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div className="muted-sm" style={{ marginBottom: 4 }}>Type</div>
          <Seg options={LESSON_TYPES as unknown as string[]} value={draft.type} onChange={(t) => set({ type: t })} />
        </div>
        <div>
          <div className="muted-sm" style={{ marginBottom: 4 }}>Difficulty</div>
          <select className="gselect" value={draft.difficulty} onChange={(e) => set({ difficulty: Number(e.target.value) })}>
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>L{n}</option>)}
          </select>
        </div>
        <div>
          <div className="muted-sm" style={{ marginBottom: 4 }}>Status</div>
          <Seg options={["live", "draft"]} value={draft.status} onChange={(s) => set({ status: s })} />
        </div>
        <div>
          <div className="muted-sm" style={{ marginBottom: 4 }}>Duration (min)</div>
          <input className="ginput" type="number" min={0} placeholder="—" value={draft.durationMinutes}
            onChange={(e) => set({ durationMinutes: e.target.value })} style={{ width: 90 }} />
        </div>
      </div>
      <button onClick={() => set({ asksTimeSaved: !draft.asksTimeSaved })}
        style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12, textAlign: "left" }}>
        <span style={{ width: 17, height: 17, borderRadius: 5, display: "grid", placeItems: "center", flex: "0 0 auto",
          background: draft.asksTimeSaved ? "var(--g-green)" : "transparent", border: `1.5px solid ${draft.asksTimeSaved ? "var(--g-green)" : "var(--g-line)"}` }}>
          {draft.asksTimeSaved && <Icon name="check" size={12} color="#04140d" />}
        </span>
        <span style={{ fontSize: 13 }}>Ask the learner if this saved them time
          <span className="muted-sm" style={{ display: "block" }}>Prompts for a weekly time-saved estimate when complete — feeds the Time saved metric.</span>
        </span>
      </button>
      <button onClick={() => set({ teachMistake: !draft.teachMistake })}
        style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12, textAlign: "left" }}>
        <span style={{ width: 17, height: 17, borderRadius: 5, display: "grid", placeItems: "center", flex: "0 0 auto",
          background: draft.teachMistake ? "var(--g-green)" : "transparent", border: `1.5px solid ${draft.teachMistake ? "var(--g-green)" : "var(--g-line)"}` }}>
          {draft.teachMistake && <Icon name="check" size={12} color="#04140d" />}
        </span>
        <span style={{ fontSize: 13 }}>L4 teach-back: plant a deliberate mistake
          <span className="muted-sm" style={{ display: "block" }}>At the teach-back level, Orbit plants one wrong assumption — passing requires the learner to catch and correct it. Off = pure clear-explanation check.</span>
        </span>
      </button>
      <input className="ginput" placeholder="Objective — what the learner should be able to do" value={draft.objective} onChange={(e) => set({ objective: e.target.value })} style={{ marginBottom: 10 }} />
      <div className="muted-sm" style={{ marginBottom: 4 }}>Prompt template <span style={{ opacity: 0.7 }}>· slots like {"{{profile.stack}}"} are filled per learner</span></div>
      <textarea className="prompt-edit" style={{ minHeight: 120, marginBottom: 12 }} placeholder="The coaching prompt that drives this lesson…" value={draft.template} onChange={(e) => set({ template: e.target.value })} spellCheck={false} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" size="sm" onClick={onCancel}>Cancel</Btn>
        <Btn variant="live" size="sm" icon="check" onClick={save}>{saving ? "Saving…" : draft.id ? "Save lesson" : "Create lesson"}</Btn>
      </div>
    </div>
  );
}

/* ===== Tracks (build + assign) ===== */
function TracksTab() {
  const { tracks, reload } = useTracks();
  const lessonsData = useLessonsAdmin();
  const catalog = lessonsData?.lessons ?? [];
  const [building, setBuilding] = useState(false);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const togglePick = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const save = () => {
    if (!name.trim() || picked.size === 0) return;
    saveTrack({ name: name.trim() }, [...picked]).then(() => {
      setBuilding(false);
      setName("");
      setPicked(new Set());
      reload();
    });
  };

  return (
    <Card title="Tracks" icon="route" iconColor="var(--g-green)" count={`${(tracks ?? []).length} tracks`}
      action={<Btn variant="live" size="sm" icon={building ? "x" : "plus"} onClick={() => setBuilding((b) => !b)}>{building ? "Cancel" : "New track"}</Btn>} pad={false}>
      {building && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--g-line)", background: "var(--g-raised)" }}>
          <input className="ginput" placeholder="Track name (e.g. AI Prototyping Fundamentals)" value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 10 }} />
          <div className="muted-sm" style={{ marginBottom: 8 }}>Pick lessons ({picked.size} selected)</div>
          <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {catalog.map((l: AdminLesson) => (
              <button key={l.id} onClick={() => togglePick(l.id)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", borderRadius: 8, textAlign: "left", background: picked.has(l.id) ? "var(--g-green-soft)" : "transparent" }}>
                <span style={{ width: 16 }}>{picked.has(l.id) ? <Icon name="check" size={14} color="var(--g-green)" /> : <Icon name="circle" size={13} color="var(--g-t3)" />}</span>
                <span style={{ flex: 1, fontSize: 13 }}>{l.title}</span>
                <span className={`level-tag ${l.type}`}>{l.type}</span>
              </button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Btn variant="ghost" size="sm" onClick={() => setBuilding(false)}>Cancel</Btn>
            <Btn variant="live" size="sm" icon="check" onClick={save}>Save track</Btn>
          </div>
        </div>
      )}
      <div style={{ padding: "5px 8px" }}>
        {(tracks ?? []).map((t) => <TrackItem key={t.id} t={t} reload={reload} />)}
        {tracks && tracks.length === 0 && !building && <div className="gt-empty">No tracks yet — build one to assign lessons to users or orgs.</div>}
      </div>
    </Card>
  );
}

/* Shared assign controls — push a track OR lesson to an org or a single user.
   Used by both the Tracks list and the Lesson catalog. */
function AssignControls({ itemType, itemId, onDone }: { itemType: "track" | "lesson"; itemId: string; onDone: () => void }) {
  const users = useUsers();
  const orgs = useOrgs();
  const [scope, setScope] = useState<"user" | "org">("org");
  const [target, setTarget] = useState("");

  const doAssign = () => {
    if (!target) return;
    assignItemReq(itemType, itemId, scope, target).then(() => { setTarget(""); onDone(); });
  };

  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
      <div className="seg g">
        {(["org", "user"] as const).map((s) => <button key={s} aria-pressed={scope === s} onClick={() => { setScope(s); setTarget(""); }}>{s === "org" ? "Org" : "User"}</button>)}
      </div>
      <select className="gselect" value={target} onChange={(e) => setTarget(e.target.value)} style={{ flex: 1 }}>
        <option value="">Select {scope}…</option>
        {scope === "org"
          ? (orgs?.orgs ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)
          : (users?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
      </select>
      <Btn variant="live" size="sm" icon="check" onClick={doAssign}>Assign</Btn>
    </div>
  );
}

function TrackItem({ t, reload }: { t: TrackRow; reload: () => void }) {
  const [assigning, setAssigning] = useState(false);
  return (
    <div style={{ borderTop: "1px solid var(--g-line)", padding: "11px 8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
        <span className="sharedg-ic"><Icon name={t.icon} size={15} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mcp-t">{t.name}</div>
          <div className="muted-sm">{t.lessonCount} lessons · {t.role}</div>
        </div>
        <Btn variant="ghost" size="sm" icon="user-plus" onClick={() => setAssigning((a) => !a)}>Assign</Btn>
        <button className="icon-mini" title="Delete" onClick={() => deleteTrackReq(t.id).then(reload)}><Icon name="trash-2" size={14} /></button>
      </div>
      {assigning && <AssignControls itemType="track" itemId={t.id} onDone={() => setAssigning(false)} />}
    </div>
  );
}
